/**
 * Theme Studio URL generation utilities
 * Based on the format: https://v2.cszglobal.com/?platform_code=888&token=87352c0033d3789a9946ac7c78d431f35cc36e333ceb3b5bfb8e6bcd9c68ea73
 */

// http://localhost:8081/

const THEME_STUDIO_BASE_URL = process.env.REACT_APP_THEME_STUDIO_URL || 'http://localhost:8081';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * Generate a simple token (hex string)
 * In production, this should call the backend API to generate a proper token
 */
const generateSimpleToken = (): string => {
  // Generate a 64-character hex token
  const chars = '0123456789abcdef';
  let token = '';
  for (let i = 0; i < 64; i++) {
    token += chars[Math.floor(Math.random() * chars.length)];
  }
  return token;
};

/**
 * Generate platform code from site name
 * Uses site name if available, otherwise generates a random number
 */
const generatePlatformCode = (siteName?: string): string => {
  if (siteName && siteName.trim()) {
    // Use site name as platform code (sanitized)
    return siteName.trim().replace(/[^a-zA-Z0-9]/g, '').toLowerCase() || '888';
  }
  // Fallback to random number (3 digits)
  return Math.floor(Math.random() * 900 + 100).toString();
};

/**
 * Generate Theme Studio URL with platform code and token
 * Format: https://v2.cszglobal.com/?platform_code=888&token=...
 */
export const generateThemeStudioUrl = (params: {
  siteName?: string;
  platformCode?: string;
  token?: string;
}): string => {
  const url = new URL(THEME_STUDIO_BASE_URL);
  
  // Use provided platform code or generate from site name
  const platformCode = params.platformCode || generatePlatformCode(params.siteName);
  url.searchParams.set('platform_code', platformCode);
  
  // Use provided token or generate a simple one
  const token = params.token || generateSimpleToken();
  url.searchParams.set('token', token);
  
  return url.toString();
};

const requestTokenFromApi = async (platformCode: string): Promise<string | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/tokens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        platform_code: platformCode,
        expiry_hours: 1,
      }),
    });

    const data = await response.json();
    if (response.ok && data?.token?.token) {
      return data.token.token;
    }
    console.error('Failed to create Theme Studio token:', data?.message || 'Unknown error');
    return null;
  } catch (error) {
    console.error('Failed to create Theme Studio token:', error);
    return null;
  }
};

/**
 * Generate Theme Studio URL with automatic token generation using backend API
 */
export const generateThemeStudioUrlWithToken = async (params: {
  siteName?: string;
  platformCode?: string;
}): Promise<string> => {
  const platformCode = params.platformCode || generatePlatformCode(params.siteName);
  let token = await requestTokenFromApi(platformCode);
  if (!token) {
    token = generateSimpleToken();
  }

  return generateThemeStudioUrl({
    siteName: params.siteName,
    platformCode,
    token,
  });
};

